import { useEffect, useState, useMemo } from "react";
import { BarChart3, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { adminTrafficStats, adminTrafficRecent } from "../../lib/api";

export function TrafficTab({ pw }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const load = async (d = days) => {
    setBusy(true);
    try {
      const [s, r] = await Promise.all([adminTrafficStats(pw, d), adminTrafficRecent(pw, 50)]);
      setStats(s); setRecent(r);
    } catch { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pw, days]);

  if (!stats) {
    return (
      <div className="surface-card p-12 text-center font-mono text-xs text-zinc-500" data-testid="traffic-tab-loading">
        {busy ? "Loading traffic…" : "No data yet."}
      </div>
    );
  }
  const max = Math.max(1, ...stats.daily.map((d) => d.views || 0));
  const tiles = [
    { l: "VIEWS TODAY", v: stats.views_today },
    { l: "VIEWS YESTERDAY", v: stats.views_yesterday },
    { l: `TOTAL · ${stats.days}D`, v: stats.total_views },
    { l: `SESSIONS · ${stats.days}D`, v: stats.unique_sessions, accent: true },
  ];
  return (
    <div className="space-y-4" data-testid="traffic-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl uppercase flex items-center gap-2"><BarChart3 size={20} /> Traffic</h2>
          <p className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">REAL-TIME PUBLIC-PAGE ANALYTICS · BOTS/CRAWLERS FILTERED</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="traffic-days-select"
            className="bg-black border border-[#222] px-2 py-1 text-xs font-mono">
            <option value={7}>7d</option><option value={30}>30d</option><option value={90}>90d</option><option value={180}>180d</option>
          </select>
          <button onClick={() => load()} disabled={busy} data-testid="traffic-refresh"
            className="btn-tc-secondary inline-flex items-center gap-2 text-xs disabled:opacity-50">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.l} className="surface-card p-5">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">{t.l}</div>
            <div className={`font-display text-4xl mt-1 ${t.accent ? "text-[var(--tc-red)]" : ""}`}>{t.v}</div>
          </div>
        ))}
      </div>

      <div className="surface-card p-5" data-testid="traffic-chart">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">/ DAILY VIEWS · LAST {days}D</div>
        <div className="flex items-end gap-[3px] h-32">
          {stats.daily.map((d) => (
            <div
              key={d.date}
              className="flex-1 bg-[var(--tc-blue)] hover:bg-[var(--tc-red)] transition-colors min-h-[2px]"
              style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }}
              title={`${d.date} · ${d.views} views · ${d.sessions} sessions`}
            />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[10px] tracking-widest text-zinc-500 mt-2">
          <span>{stats.daily[0]?.date || ""}</span>
          <span>{stats.daily[stats.daily.length - 1]?.date || ""}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="surface-card p-5" data-testid="traffic-top-paths">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">/ TOP PAGES</div>
          {stats.top_paths.length === 0 ? <div className="text-zinc-500 font-mono text-xs">No data.</div> :
            <ul className="space-y-1">
              {stats.top_paths.map((p) => (
                <li key={p.path} className="flex justify-between items-center bg-black border border-[#222] px-3 py-2 font-mono text-xs">
                  <span className="truncate flex-1 mr-3 text-zinc-300">{p.path}</span>
                  <span className="text-[var(--tc-red)]">{p.views}</span>
                </li>
              ))}
            </ul>
          }
        </div>
        <div className="surface-card p-5" data-testid="traffic-top-referrers">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">/ TOP REFERRERS</div>
          {stats.top_referrers.length === 0 ? <div className="text-zinc-500 font-mono text-xs">No external referrers in this window.</div> :
            <ul className="space-y-1">
              {stats.top_referrers.map((r) => (
                <li key={r.host} className="flex justify-between items-center bg-black border border-[#222] px-3 py-2 font-mono text-xs">
                  <span className="truncate flex-1 mr-3 text-zinc-300">{r.host}</span>
                  <span className="text-[var(--tc-red)]">{r.views}</span>
                </li>
              ))}
            </ul>
          }
        </div>
      </div>

      {stats.top_utm_sources.length > 0 && (
        <div className="surface-card p-5" data-testid="traffic-top-utm">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">/ TOP UTM SOURCES</div>
          <div className="flex flex-wrap gap-2">
            {stats.top_utm_sources.map((u) => (
              <span key={u.source} className="chip border-[var(--tc-blue)] text-[var(--tc-blue)]">{u.source} · {u.views}</span>
            ))}
          </div>
        </div>
      )}

      <div className="surface-card overflow-x-auto" data-testid="traffic-recent">
        <div className="p-4 border-b border-[#222] font-mono text-[10px] tracking-widest text-zinc-500">RECENT VIEWS · 50 LATEST</div>
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-3">WHEN</th><th className="p-3">PATH</th>
              <th className="p-3">REFERRER</th><th className="p-3">UTM</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-zinc-500 font-mono text-xs">No views yet.</td></tr>}
            {recent.map((r, i) => (
              <tr key={i} className="border-b border-[#222]">
                <td className="p-3 font-mono text-[10px] text-zinc-300">{new Date(r.at).toLocaleString()}</td>
                <td className="p-3 font-mono text-xs text-white truncate max-w-xs">{r.path}</td>
                <td className="p-3 font-mono text-[10px] text-zinc-400">{r.referrer_host || "—"}</td>
                <td className="p-3 font-mono text-[10px] text-zinc-400">{r.utm_source || r.utm_campaign || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== AUDIT LOG TAB =====

export default TrafficTab;
